import { useCallback, useEffect, useState } from 'react';

import { useAuth } from '@auth/AuthProvider';
import { HTTPResponse } from '@data/controller/NetworkController';
import PeopleController, { PeopleSearchField } from '@data/controller/PeopleController';
import { PeopleSearchResult, Person } from '@interfaces/person.interface';

type PageFetcher = (lastPersonID: string | null) => Promise<HTTPResponse<PeopleSearchResult>>;

// The cursor walk both the full list and search results share. Pass null to sit
// idle without issuing requests.
function usePeoplePages(fetchPage: PageFetcher | null) {
  const [people, setPeople] = useState<Person[]>([]);
  const [loading, setLoading] = useState<boolean>(fetchPage !== null);
  const [hasLoaded, setHasLoaded] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const [hasMore, setHasMore] = useState<boolean>(false);
  const [nextLastPersonID, setNextLastPersonID] = useState<string | null>(null);
  const [isLoadingMore, setIsLoadingMore] = useState<boolean>(false);

  // Which fetcher the results in hand came from. Clearing in the effect leaves
  // one render where a new fetcher is paired with the old results, which is
  // long enough to see.
  const [loadedFor, setLoadedFor] = useState<PageFetcher | null>(null);

  const load = useCallback(async (lastPersonID: string | null) => {
    if (!fetchPage) return;

    const isFirstPage = lastPersonID === null;

    if (isFirstPage) {
      setLoading(true);
    } else {
      setIsLoadingMore(true);
    }

    setError(null);

    const response = await fetchPage(lastPersonID);
    const body = response.body;

    if (response.error || !body) {
      setError(response.error || 'Failed to load people.');

      if (isFirstPage) {
        setPeople([]);
        setHasMore(false);
        setNextLastPersonID(null);
      }
    } else {
      const page = body.results ?? [];

      // Appending rather than replacing is what makes this a cursor walk; the
      // first page still replaces so a refetch doesn't duplicate rows.
      setPeople((currentPeople) => isFirstPage ? page : [...currentPeople, ...page]);
      setHasMore(body.hasMore === true && Boolean(body.nextLastPersonID));
      setNextLastPersonID(body.nextLastPersonID ?? null);
      setLoadedFor(() => fetchPage);
    }

    if (isFirstPage) {
      setLoading(false);
      setHasLoaded(true);
    } else {
      setIsLoadingMore(false);
    }
  }, [fetchPage]);

  useEffect(() => {
    // Going idle keeps the last results on screen: the view that owns them is
    // still fading out, and blanking them mid-fade reads as a flicker.
    if (!fetchPage) {
      setLoading(false);
      return;
    }

    // A different fetcher means a different query, so previous results are
    // cleared rather than lingering under the new one.
    setPeople([]);
    setHasLoaded(false);
    setHasMore(false);
    setNextLastPersonID(null);
    setError(null);

    void load(null);
  }, [fetchPage, load]);

  const loadMore = useCallback(() => {
    if (!hasMore || isLoadingMore || !nextLastPersonID) return;

    void load(nextLastPersonID);
  }, [hasMore, isLoadingMore, nextLastPersonID, load]);

  const refetch = useCallback(() => load(null), [load]);

  // Idle keeps whatever was last shown so a fading-out view doesn't blank.
  const isShowingCurrent = fetchPage === null || loadedFor === fetchPage;

  return {
    people: isShowingCurrent ? people : [],
    loading: loading || (fetchPage !== null && !isShowingCurrent),
    hasLoaded: isShowingCurrent && hasLoaded,
    error,
    hasMore: isShowingCurrent && hasMore,
    isLoadingMore,
    loadMore,
    refetch
  } as const;
}

export default function usePeople() {
  const auth = useAuth();
  const churchID = auth.churchId;

  const fetchPage = useCallback((lastPersonID: string | null) => {
    return PeopleController.fetchPeople(churchID!, auth, lastPersonID);
  }, [churchID, auth]);

  return usePeoplePages(churchID ? fetchPage : null);
}

// A query with an @ is treated as an email; anything else searches names.
export function peopleSearchFieldFor(query: string): PeopleSearchField {
  return query.includes('@') ? 'email' : 'name';
}

const SEARCH_DEBOUNCE_MS = 300;

export function usePeopleSearch(query: string) {
  const auth = useAuth();
  const churchID = auth.churchId;
  const trimmedQuery = query.trim();

  // Debounced so typing doesn't fire a request per keystroke.
  const [debouncedQuery, setDebouncedQuery] = useState(trimmedQuery);

  useEffect(() => {
    const timeout = window.setTimeout(() => setDebouncedQuery(trimmedQuery), SEARCH_DEBOUNCE_MS);

    return () => window.clearTimeout(timeout);
  }, [trimmedQuery]);

  const fetchPage = useCallback((lastPersonID: string | null) => {
    return PeopleController.searchPeople(
      churchID!,
      auth,
      peopleSearchFieldFor(debouncedQuery),
      debouncedQuery,
      lastPersonID
    );
  }, [churchID, auth, debouncedQuery]);

  const results = usePeoplePages(churchID && debouncedQuery.length > 0 ? fetchPage : null);

  // True while the user has typed something the debounce hasn't caught up to.
  const isPending = trimmedQuery !== debouncedQuery;

  // Results belong to the previous query while a new one is still settling, so
  // they read as loading rather than as answers. Clearing the field is the one
  // pending state that keeps them: that view is fading out, and blanking it
  // mid-fade is the flicker this avoids.
  const isStale = isPending && trimmedQuery.length > 0;

  return {
    ...results,
    people: isStale ? [] : results.people,
    hasLoaded: isStale ? false : results.hasLoaded,
    loading: isStale || results.loading,
    hasMore: isStale ? false : results.hasMore,
    field: peopleSearchFieldFor(isStale ? trimmedQuery : debouncedQuery),
    isPending
  } as const;
}
